import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { submitFeedback } from '@verzekeringapp-mono/shared/src/api/feedback'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { getUser } from '@verzekeringapp-mono/shared/src/state/selectors/user'
import { getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import Button from './Button'
import TextInput from './TextInput'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Modal from './Modal'

type Props = {
  onFeedbackSend: (responseType: string) => void
  onCancel: () => void
}

const feedbackIconClassNames = {
  promoter: 'text-sentiment-positive',
  passive: 'text-sentiment-neutral',
  detractor: 'text-sentiment-negative'
} as const

export default function FeedbackModal ({ onFeedbackSend, onCancel }: Props) {
  const user = useAppSelector(getUser)
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const [isLoading, setIsLoading] = React.useState(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid }
  } = useForm({
    defaultValues: {
      responseType: '',
      response: ''
    }
  })

  const responseType = watch('responseType')

  const sendFeedback = useCallback(async (responseType: string, response: string) => {
    if (!user || !resourceAuthorization) {
      return false
    }

    setIsLoading(true)

    try {
      await submitFeedback(user.id, resourceAuthorization.relationNumber, responseType, response)
    } catch (error) {
      console.log('Error submitting feedback', error)
    } finally {
      setIsLoading(false)
    }

    return true
  }, [user, resourceAuthorization])

  const onSend = useCallback(async (responseType: string, response: string) => {
    const success = await sendFeedback(responseType, response)

    if (success) {
      onFeedbackSend(responseType)
    } else {
      onCancel()
    }
  }, [user, resourceAuthorization, sendFeedback])

  const onDeclined = useCallback(async () => {
    sendFeedback('declined', 'Geweigerd')

    onCancel()
  }, [user, resourceAuthorization, sendFeedback])

  return (
    <Modal onClose={onDeclined}>
      <div className='flex flex-col gap-4 align-middle items-center w-full'>
        <p className='text-lg font-semibold'>
        Help ons verbeteren!
        </p>

        <p className='mb-2'>
        Hoe tevreden ben je over deze applicatie?
        </p>

        <Controller
          control={control}
          name='responseType'
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <div className='flex justify-between w-full'>
              <button
                type='button'
                onClick={() => onChange('promoter')}
              >
                <FontAwesomeIcon
                  icon={[`${value === 'promoter' ? 'fas' : 'far'}`, 'face-smile']}
                  size='5x'
                  className={`${feedbackIconClassNames.promoter}`}
                />
              </button>

              <button
                type='button'
                onClick={() => onChange('passive')}
              >
                <FontAwesomeIcon
                  icon={[`${value === 'passive' ? 'fas' : 'far'}`, 'face-meh']}
                  size='5x'
                  className={`${feedbackIconClassNames.passive} ${value === 'passive' ? '' : 'opacity-50'}`}
                />
              </button>

              <button
                type='button'
                onClick={() => onChange('detractor')}
              >
                <FontAwesomeIcon
                  icon={[`${value === 'detractor' ? 'fas' : 'far'}`, 'face-frown']}
                  size='5x'
                  className={`${feedbackIconClassNames.detractor} ${value === 'detractor' ? '' : 'opacity-50'}`}
                />
              </button>
            </div>
          )}
        />

        {
          responseType !== '' && (
            <div className='w-full'>
              <p className='mb-2 text-center'>
                {responseType === 'promoter' && 'Wat vind je zo goed?'}

                {responseType === 'passive' && 'Hoe kunnen wij je ervaring verbeteren?'}

                {responseType === 'detractor' && 'Hoe kunnen wij je ervaring verbeteren?'}
              </p>

              <Controller
                control={control}
                name='response'
                render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                  <TextInput
                    type='textarea'
                    placeholder=''
                    className='w-full min-h-[200px] text-start align-top'
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                    value={value}
                    isValid={!invalid}
                  />
                )}
              />
            </div>
          )}

        { responseType !== '' && (
          <Button
            className='w-full'
            onClick={handleSubmit((data) => {
              if (!data.responseType) {
                return
              }

              onSend(data.responseType, data.response)
            })}
            disabled={!isValid}
            isLoading={isLoading}
          >
          Versturen
          </Button>
        )}
      </div>
    </Modal>
  )
}
